/**
 * Welche Einstellungen ein Konto überhaupt hat. (19.10.2026)
 *
 * Martin, in Alberts Konto: *„Er hat zum Beispiel kein Live. Dementsprechend können auch die ganzen Einstellungen aus seinem
 * System ausgeblendet werden, da sie nur verwirren würden.“*
 *
 * DIE eine Stelle: Seite UND Server fragen hier. Rein (keine Oberfläche, kein Speicher).
 *
 *  - Anzeige: frontend/lib/konto-lage.ts reicht die Lage des angemeldeten Kontos hierher; die Einstellungsseite zeichnet nur,
 *    was `verfuegbarFuerKonto` freigibt.
 *  - Server: `einstellungenSchreiben` (server/einstellungen-zeit.ts) lässt ein Feld, das das Konto nicht hat, nicht durch
 *    (es landet in `verworfen`). Ausblenden allein genügt nicht — ein Gerät mit altem Stand oder Vespera auf Zuruf käme sonst
 *    an einen toten Schalter, der still etwas zu speichern scheint.
 *
 * ## Die Regeln
 *
 *  - Anruf/Live: nur, wer den Anruf im Paket hat (server/entitlements.ts, `anrufOffenFuer` → `features.call`). Ohne ihn gibt es
 *    weder die Wahl des Live-Modells noch die Anruf-Stimme noch „Anrufe von Vespera“.
 *  - Hauptkonto (Konto 1): die Seite „Erweitert“ (Versuche), Entwickler-Team, Morgenreport, „Modell für die tiefen Auswertungen“ (der Server liest es nur aus den
 *    Einstellungen von Konto 1 — bei jedem anderen Konto wäre der Schalter tot).
 *
 * ⚠️ Der sparsame Fall ist die Vorgabe: ohne Wissen über das Konto bleibt alles Ausgeblendete aus (zeigt zu wenig, nie zu viel).
 * ⚠️ Konto 1 verliert nichts: für ihn ist jede Gruppe `true` (Gegenprobe in tests/frontend/einstellungen-konto-sichtbar.test.ts).
 */

/** Konto 1 (Betreiber, SUPERUSER_ID in server/werkzeug-bereiche.ts) — shared/ darf den Server nicht einbinden. */
export const HAUPTKONTO = 1;

export interface KontoLage {
  uid: number | null | undefined;
  /** Hat das Konto den Anruf/Live (features.call aus /api/auth/me, serverseitig `anrufOffenFuer`)? */
  anruf: boolean;
}

export type EinstGruppe =
  /** Auswahl des Live-Modells („Im Anruf“ auf der Sprechen-Seite). */
  | "anrufModell"
  /** Die Gemini-Stimmen des Live-Anrufs (Stimme und Vorlesen). */
  | "anrufStimme"
  /** „Vespera darf mich anrufen“, Ruhezeit, Tageslimit (Mitteilungen). */
  | "anrufeVonVespera"
  /** „Modell für die tiefen Auswertungen“ und die Denktiefe (Modelle). */
  | "tiefeAuswertung"
  /** Die Seite „Entwickler-Team“ samt Schnellschalter und Lage-Karte. */
  | "entwickler"
  /** Der Morgenreport (Menü-Eintrag, Chat-Karte; kein Feld in den Einstellungen). */
  | "morgenreport"
  /** Die Seite „Erweitert“ (Versuche: Vorlesen im Hintergrund, Mikro beim Vorlesen offen, Bildrate, Ruckel-Test …). */
  | "versuche"
  /** Stimme und „schon gesehen“ der Einführung von Vespera 9.0 (kein Schalter in den Einstellungen; die Seite /neun schreibt sie). */
  | "neun";

interface GruppenRegel {
  /** Wann die Gruppe da ist. */
  gilt: (l: KontoLage) => boolean;
  /** Die Felder von `users.settings`, die nur mit dieser Gruppe etwas bewirken — der Server nimmt sie sonst nicht an. */
  felder: readonly string[];
}

const hauptkonto = (l: KontoLage): boolean => l.uid === HAUPTKONTO;
const anruf = (l: KontoLage): boolean => l.anruf === true;

const REGELN: Readonly<Record<EinstGruppe, GruppenRegel>> = {
  anrufModell: { gilt: anruf, felder: ["realtimeModel"] },
  /*
   * ⚠️ Kein Feld: `realtimeVoice` ist zugleich der Ausgangswert der Vorlese-Stimme (Gemini-Chirp). Der Server nimmt es an;
   *    die Seite zeigt ohne Anruf nur die Vorlese-Stimme.
   */
  anrufStimme: { gilt: anruf, felder: [] },
  anrufeVonVespera: {
    gilt: (l) => hauptkonto(l) && anruf(l),
    felder: ["anrufErlaubt", "anrufRuheAb", "anrufRuheBis", "anrufTageslimit"],
  },
  tiefeAuswertung: { gilt: hauptkonto, felder: ["starkesModell", "tiefeDenkstufe"] },
  entwickler: {
    gilt: hauptkonto,
    felder: [
      "entwicklerImChat",
      "entwicklerGesamt",
      "zimmermannImChat",
      "hueterImChat",
      "serverNetz",
      "selbstverbesserung",
      "entwicklerVorlesen",
      "zusatzVorlesen",
      "schmiedStimmeGanz",
      "entwicklerStimme",
      "zimmermannStimme",
      "hueterStimme",
      "entwicklerTempo",
      "zimmermannTempo",
      "hueterTempo",
      "entwicklerAusfuehrlichkeit",
      "zimmermannAusfuehrlichkeit",
      "hueterAusfuehrlichkeit",
      // 06.10.2026: „Kurz Bescheid geben …“ und „Morse im Menü“ gelten nur im Hauptkonto (shared/warte-satz.ts, shared/morse-konto.ts).
      "warteSatz",
      "morseLernen",
    ],
  },
  morgenreport: { gilt: hauptkonto, felder: [] },
  /*
   * 20.10.2026 (Martin, Alberts Konto): die Versuche sind Martins Prüfstand, Albert würden sie nur verwirren.
   * ⚠️ Kein Feld: der Server lehnt gespeicherte Werte nicht ab (alte Geräte, Zuruf). Dafür liest die App sie in anderen
   *    Konten als AUS, wo sie wirken: Hintergrund-Vorlesen (vorlesen-nativ.ts), „Mikro beim Vorlesen offen“ (app.ts,
   *    mikroOffenGilt) und der Ruckel-Test (ruckel-schalter.ts). Der gespeicherte Wert wird nicht überschrieben.
   *    Die Bildrate ist nur eine Anzeige ohne Schalter.
   */
  versuche: { gilt: hauptkonto, felder: [] },
  neun: { gilt: hauptkonto, felder: ["neunStimme", "neunEinfuehrung"] },
};

/** Alle Gruppen für eine Lage: sichtbar ja/nein. */
export function verfuegbarFuerKonto(lage: KontoLage): Record<EinstGruppe, boolean> {
  const aus = {} as Record<EinstGruppe, boolean>;
  for (const g of Object.keys(REGELN) as EinstGruppe[]) aus[g] = REGELN[g].gilt(lage);
  return aus;
}

/** Hat dieses Konto das Feld? Ein Feld ohne Gruppe hat jedes Konto. */
export function feldErlaubtFuerKonto(lage: KontoLage, feld: string): boolean {
  for (const g of Object.keys(REGELN) as EinstGruppe[]) {
    if (REGELN[g].felder.includes(feld)) return REGELN[g].gilt(lage);
  }
  return true;
}
